import { tracking } from '@iwoca/frontend-tracking-library';
import { stringify } from 'qs';
import { useNavigate } from 'react-router';

import { trackLeadQuality } from './trackLeadQuality';
import { useIsInStep0EligibilityTest } from '../../AnonymousSplitTest/useIsInStep0EligibilityTest';
import { fetchPutAndGetState } from '../../api/lendingApi/lendingApiMutations';
import { useGetStateQuery } from '../../api/lendingApi/lendingApiQueries';
import { getMaxReachedStep } from '../../helpers/getMaxReachedStep';
import { getQueryParams } from '../../helpers/getQueryParams';
import { resolveVariantRoute } from '../../helpers/resolveVariantRoute';
import {
  TGetStateResponse,
  TPutStateRequestBody,
  TSignupStepsUK,
} from '../../types';
import { ACCOUNT_PAGES_URL } from '../../urls';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import logger from '../../utils/logger';
import { useContextStateKey } from '../useContextStateKey';
import { useCurrentStep } from '../useCurrentStep';
import { useSignupParams } from '../useSignupParams';
import { useSplitTestContext } from '../useSplitTestContext';
import { useImpersonatedStateKey } from '../useStateKey';

export const useSignupRouterUK = () => {
  const { rawAffiliateParam, isDirectCustomer } = useSignupParams();
  const navigate = useNavigate();

  const isInStep0EligibilityTest = useIsInStep0EligibilityTest();
  const { hasAIQuestionSignupEnabled } = useSplitTestContext();
  const aiEnabled = isDirectCustomer && hasAIQuestionSignupEnabled;

  const { state } = useGetStateQuery<TGetStateResponse>();

  const basePath = `/${rawAffiliateParam}`;

  const currentStep = useCurrentStep();

  const stateKey = useContextStateKey();
  const impersonatedStateKey = useImpersonatedStateKey();

  const navigateSignup = async ({
    route: requestedRoute,
    updatedState,
    beforeNavigate,
  }: {
    route: TSignupStepsUK;
    updatedState?: TPutStateRequestBody['data'];
    beforeNavigate?: () => void | Promise<void>;
  }) => {
    if (!stateKey) {
      return logger.error(
        'Attempted to navigate without stateKey. StateKey is required for the PUT to state.',
      );
    }

    const route = isInStep0EligibilityTest
      ? resolveVariantRoute({ currentStep, requestedRoute, aiEnabled })
      : requestedRoute;

    let newState: TPutStateRequestBody['data'] = updatedState || state;

    if (route === 'decision') {
      tracking.ampli.signupCompleted(getAmplitudeDefaultEventProperties());

      newState = {
        ...newState,
        ui: {
          ...newState.ui,
          max_reached_step: 'myAccount',
          activeStep: {
            id: 'myAccount',
          },
        },
      };
    }

    if (route !== 'new' && newState.ui?.activePartnerStep !== route) {
      const uiState = newState.ui || {};
      uiState.activePartnerStep = route;

      if (!newState.ui?.max_reached_step) {
        uiState.max_reached_step = route;
      } else if (
        newState.ui?.max_reached_step !== route &&
        newState.ui?.max_reached_step !== 'myAccount'
      ) {
        const updatedFurthestStep = getMaxReachedStep(
          newState.ui?.max_reached_step,
          route,
          isDirectCustomer,
          isInStep0EligibilityTest,
        );

        uiState.max_reached_step = updatedFurthestStep;
      }

      const putStateBody: TPutStateRequestBody = {
        data: {
          ...newState,
          ui: uiState,
        },
      };

      await fetchPutAndGetState({ body: putStateBody, stateKey });
    }

    beforeNavigate?.();

    const newQueryParams = {
      ...getQueryParams(),
    };

    if (impersonatedStateKey) {
      newQueryParams.state_key = impersonatedStateKey;
    }

    // Paid Search Marketing requires state to be saved before fetching the lead quality data.
    // Keyed on requestedRoute, not route, so the event fires on the same user action in both arms.
    if (
      isDirectCustomer &&
      currentStep === 'business' &&
      requestedRoute === 'check-eligibility'
    ) {
      await trackLeadQuality();
    }

    let newPath = `${route}/`;
    if (stringify(newQueryParams)) {
      newPath = `${route}/?${stringify(newQueryParams)}`;
    }

    navigate(`${basePath}/${newPath}`);
  };

  const navigateSignupWithoutStateUpdate = (newRoute: TSignupStepsUK) => {
    const newQueryParams = {
      ...getQueryParams(),
    };

    if (impersonatedStateKey) {
      newQueryParams.state_key = impersonatedStateKey;
    }

    let newPath = `${newRoute}/`;
    if (stringify(newQueryParams)) {
      newPath = `${newRoute}/?${stringify(newQueryParams)}`;
    }

    navigate(`${basePath}/${newPath}`);
  };

  const navigateToAccountPages = async (
    stateKey: string,
    updatedState?: TPutStateRequestBody['data'],
  ) => {
    const newState = updatedState || state;

    const putStateBody: TPutStateRequestBody = {
      data: {
        ...newState,
        ui: {
          ...newState.ui,
          max_reached_step: 'myAccount',
          activeStep: {
            id: 'myAccount',
          },
        },
      },
    };

    await fetchPutAndGetState({ body: putStateBody, stateKey });

    tracking.ampli.signupCompleted(getAmplitudeDefaultEventProperties());

    window.location.href = ACCOUNT_PAGES_URL;
  };

  return {
    basePath,
    currentStep,
    navigateSignup,
    navigateSignupWithoutStateUpdate,
    navigateToAccountPages,
  };
};
